import React, { useState, useMemo } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { ASSETS, CONFERENCE, LATE_REGISTRATION, REGISTRATION_FEES, REGISTRATION_MAIL, ZELLE, PAYMENT_METHODS, BANQUET_TICKET_PRICE, SPONSORSHIP_TIERS, JOURNAL_AD_SIZES } from '../mock';
import { Calendar, MapPin, Ticket, Award, Crown, Star, FileText, Download, CheckCircle2, Hotel, ExternalLink, Smartphone, Plus, Minus, BookOpen, AlertTriangle, Car } from 'lucide-react';
import { toast } from 'sonner';
import { AddToCalendar, AddFullConference } from '../components/AddToCalendar';
import { HappeningNow } from '../components/HappeningNow';
import { ConferencePhotos } from '../components/ConferencePhotos';
import { ConferenceRecap } from '../components/ConferenceRecap';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LATE = LATE_REGISTRATION.active;
const LATE_FEE = LATE ? LATE_REGISTRATION.fee : 0;
const SALES_CLOSED = false;
const RECAP = CONFERENCE.recap.active;
const journalOpen = Date.now() >= new Date(ASSETS.journalReleaseAt).getTime();

function LateNotice() {
  return (
    <div className="mb-8 rounded-md border-2 border-red-500/70 bg-red-950/40 p-6" data-testid="late-registration-notice">
      <div className="flex items-center gap-2 text-red-300 font-heading tracking-widest text-xs mb-2"><AlertTriangle size={16} /> IMPORTANT NOTICE</div>
      <h3 className="font-display text-3xl text-cbtu-cream mb-2">REGISTRATION IS NOW CLOSED</h3>
      <p className="text-cbtu-text/90 leading-relaxed">
        If you still need to register, a <span className="text-gold font-semibold">${LATE_REGISTRATION.fee} late fee</span> is added to every registration.
        Late registration is available online below, or on-site at the conference on <span className="text-gold font-semibold">{LATE_REGISTRATION.onsiteDate}</span>.
      </p>
      <div className="mt-4 rounded-sm border border-gold/60 bg-cbtu-green/30 px-4 py-3 text-cbtu-cream" data-testid="onsite-registration-notice">
        <div className="font-heading tracking-widest text-gold text-sm mb-1">PAY ON SITE</div>
        Conference registration can now be paid <span className="text-gold font-semibold">on site for $200.00</span> at the registration desk (Foyer).
        <span className="block mt-1"><span className="text-gold font-semibold">Banquet tickets are still available</span> and can be purchased on site at registration.</span>
      </div>
      <p className="text-cbtu-muted text-sm mt-2">Sponsorships and journal ads are still available, but they will appear in the digital ad journal on the CBTU Region III website, not the printed journal.</p>
      <div className="mt-4"><HotelNotice /></div>
    </div>
  );
}

function HotelNotice() {
  return (
    <div className="mb-4 rounded-sm border border-gold/50 bg-cbtu-green/30 px-4 py-3 flex items-start gap-2 text-sm text-cbtu-cream" data-testid="hotel-block-notice">
      <Hotel size={16} className="text-gold mt-0.5 flex-shrink-0" />
      <span><span className="text-gold font-semibold">Hotel block rate:</span> Please contact the DoubleTree by Hilton Jacksonville Riverfront to see if they will honor the block room rate.</span>
    </div>
  );
}

function ClosedTag({ testId }) {
  return <div className="block text-center font-heading text-xs tracking-widest py-2.5 border-t border-gold/30 bg-cbtu-black/60 text-cbtu-muted" data-testid={testId}>CLOSED</div>;
}

const tierIcon = { 'Presenting Sponsor': Crown, 'Platinum Sponsor': Star, 'Gold Sponsor': Star, 'Silver Sponsor': Star, 'Bronze Sponsor': Star };
const tierColor = { 'Presenting Sponsor': 'from-amber-300 to-amber-600', 'Platinum Sponsor': 'from-slate-200 to-slate-400', 'Gold Sponsor': 'from-yellow-300 to-amber-500', 'Silver Sponsor': 'from-gray-200 to-gray-400', 'Bronze Sponsor': 'from-orange-400 to-orange-700' };

function ZellePaymentCard({ compact = false, title = 'Pay with Zelle or Cash App' }) {
  return (
    <div className="card-dark rounded-md overflow-hidden border border-gold/40">
      <div className="bg-cbtu-green/60 px-4 py-3 border-b border-gold/30 flex items-center gap-2">
        <Smartphone size={16} className="text-gold" />
        <div className="font-heading tracking-widest text-gold text-xs">{title.toUpperCase()}</div>
      </div>
      <div className={`p-4 grid ${compact ? 'sm:grid-cols-[180px_1fr]' : 'md:grid-cols-[260px_1fr]'} gap-4 items-center`}>
        <a href={ZELLE.image} target="_blank" rel="noreferrer" className="block rounded-md overflow-hidden hover:opacity-90 transition-opacity" aria-label="Open Zelle and Cash App QR codes full-size">
          <img src={ZELLE.image} alt="CBTU Region 3 Payment Options - Zelle (cbturegioniii@gmail.com) and Cash App ($cbturegionIII) QR codes" className="w-full h-auto" loading="lazy" />
        </a>
        <div>
          <div className="font-display text-lg gold-text-fill mb-2">{ZELLE.name}</div>
          <ol className="text-xs text-cbtu-text/85 space-y-2 list-decimal list-inside">
            <li>Open <span className="text-gold">Zelle</span> or <span className="text-gold">Cash App</span> on your phone</li>
            <li>Scan the matching QR code above</li>
            <li>
              Or send directly to:
              <ul className="list-none pl-4 mt-1 space-y-1">
                <li><span className="text-gold-bright">Zelle:</span> <a href={`mailto:${ZELLE.email}`} className="text-cbtu-cream hover:underline break-all">{ZELLE.email}</a></li>
                <li><span className="text-gold-bright">Cash App:</span> <span className="text-cbtu-cream">$cbturegionIII</span></li>
              </ul>
            </li>
          </ol>
        </div>
      </div>
    </div>
  );
}

export default function Conference() {
  const [reg, setReg] = useState({
    name: '', email: '', phone: '', union: '', chapter: '',
    category: 'Member Registration',
    includeRegistration: true,
    banquetQty: 0,
    sponsorship: '',
    journalAd: '',
    paymentMethod: '',
    notes: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const basePrice = REGISTRATION_FEES.find((f) => f.category === reg.category)?.price || 0;
  const regFee = basePrice + LATE_FEE;
  const sponsorshipPrice = SPONSORSHIP_TIERS.find((s) => s.tier === reg.sponsorship)?.price || 0;
  const journalPrice = JOURNAL_AD_SIZES.find((j) => j.size === reg.journalAd)?.price || 0;
  const banquetTotal = (reg.banquetQty || 0) * BANQUET_TICKET_PRICE;

  const total = useMemo(() => {
    return (reg.includeRegistration ? regFee : 0) + banquetTotal + sponsorshipPrice + journalPrice;
  }, [reg.includeRegistration, regFee, banquetTotal, sponsorshipPrice, journalPrice]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!reg.name || !reg.email) {
      toast.error('Please complete name and email.');
      return;
    }
    if (!reg.includeRegistration && reg.banquetQty === 0 && !reg.sponsorship && !reg.journalAd) {
      toast.error('Please select at least one item to pay for.');
      return;
    }
    const items = [];
    if (reg.includeRegistration) items.push({ type: 'registration', label: reg.category, quantity: 1, unit_price: basePrice, amount: basePrice });
    if (reg.includeRegistration && LATE_FEE) items.push({ type: 'late_fee', label: 'Late Registration Fee', quantity: 1, unit_price: LATE_FEE, amount: LATE_FEE });
    if (reg.banquetQty > 0) items.push({ type: 'banquet', label: 'Banquet Ticket', quantity: reg.banquetQty, unit_price: BANQUET_TICKET_PRICE, amount: banquetTotal });
    if (reg.sponsorship) items.push({ type: 'sponsorship', label: reg.sponsorship, quantity: 1, unit_price: sponsorshipPrice, amount: sponsorshipPrice });
    if (reg.journalAd) items.push({ type: 'journal_ad', label: reg.journalAd, quantity: 1, unit_price: journalPrice, amount: journalPrice });

    setSubmitting(true);
    try {
      await axios.post(`${API}/registration`, {
        name: reg.name,
        email: reg.email,
        phone: reg.phone,
        union: reg.union,
        chapter: reg.chapter,
        category: reg.category,
        fee: reg.includeRegistration ? regFee : 0,
        payment_method: reg.paymentMethod,
        items,
        total_paid: total,
        notes: reg.notes,
      });
      toast.success(`Registration received for ${reg.name}. Total: $${total}. We will follow up with payment instructions.`);
      setReg({
        name: '', email: '', phone: '', union: '', chapter: '',
        category: 'Member Registration', includeRegistration: true,
        banquetQty: 0, sponsorship: '', journalAd: '', paymentMethod: '', notes: '',
      });
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Could not submit your registration. Please try again or mail in the form.';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fade-in-up">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 py-12 grid lg:grid-cols-[1fr_420px] gap-10 items-center">
          <a href={CONFERENCE.featuredImage.src} target="_blank" rel="noreferrer" className="block lg:order-2 max-w-md mx-auto w-full" data-testid="conference-featured-image">
            <img src={CONFERENCE.featuredImage.src} alt={CONFERENCE.featuredImage.alt} className="w-full h-auto rounded-md gold-border shadow-2xl" />
          </a>
          {RECAP ? (
          <div>
            <ConferenceRecap />
            <div className="flex flex-wrap gap-3 mt-6">
              <a href="#photos" className="gold-button px-6 py-3 rounded-sm inline-flex items-center gap-2" data-testid="recap-photos-btn"><Star size={16} /> Conference Photos</a>
              <Link to="/digital-journal" className="ghost-button px-6 py-3 rounded-sm inline-flex items-center gap-2" data-testid="recap-journal-btn"><BookOpen size={16} /> Digital Ad Journal</Link>
              <a href="#agenda" className="ghost-button px-6 py-3 rounded-sm inline-flex items-center gap-2" data-testid="recap-agenda-btn"><FileText size={16} /> Agenda</a>
            </div>
          </div>
          ) : (
          <div>
            <h1 className="font-display text-5xl md:text-6xl leading-[0.95] mb-4">
              <span className="gold-text-fill">REGION III</span><br/>
              <span className="text-cbtu-cream">REGIONAL</span><br/>
              <span className="gold-text-fill">CONFERENCE</span>
            </h1>
            <p className="font-serif-script text-2xl text-cbtu-cream mb-5">“{CONFERENCE.theme}”</p>
            <ul className="space-y-2 text-cbtu-cream mb-6">
              <li className="flex items-center gap-2"><Calendar size={18} className="text-gold" /> {CONFERENCE.dates}</li>
              <li className="flex items-center gap-2"><Hotel size={18} className="text-gold" /> {CONFERENCE.venue}</li>
              <li className="flex items-center gap-2"><MapPin size={18} className="text-gold" /> {CONFERENCE.address}</li>
            </ul>
            <div className="flex flex-wrap gap-3">
              <a href="#register" className="gold-button px-6 py-3 rounded-sm inline-flex items-center gap-2" data-testid="hero-register-btn"><Ticket size={16} /> {LATE ? 'Late Registration' : 'Register Now'}</a>
              <a href="#parking" className="ghost-button px-6 py-3 rounded-sm inline-flex items-center gap-2" data-testid="hero-parking-btn"><Car size={16} /> Parking Info</a>
              {!SALES_CLOSED && <a href="#sponsor" className="ghost-button px-6 py-3 rounded-sm inline-flex items-center gap-2"><Award size={16} /> Become a Sponsor</a>}
            </div>
          </div>
          )}
        </div>
      </section>

      {/* Quick info bar */}
      {!RECAP && <section className="green-banner">
        <div className="max-w-7xl mx-auto px-4 py-6 grid sm:grid-cols-3 gap-4 text-center text-cbtu-cream">
          <div><div className="font-display text-2xl gold-text-fill">4</div><div className="text-xs tracking-widest uppercase">Days of Programming</div></div>
          <div><div className="font-display text-2xl gold-text-fill">$125</div><div className="text-xs tracking-widest uppercase">Individual Banquet Ticket</div></div>
          <div><div className="font-display text-2xl gold-text-fill">7 Chapters</div><div className="text-xs tracking-widest uppercase">United Across the South</div></div>
        </div>
      </section>}

      <HappeningNow agenda={CONFERENCE.agenda} />
      <ConferencePhotos />

      {/* Complimentary Parking */}
      <section id="parking" className="max-w-7xl mx-auto px-4 pt-14 scroll-mt-28" data-testid="parking-section">
        <div className="rounded-md overflow-hidden border-2 border-gold/70 bg-gradient-to-br from-[#0f1a3a] to-cbtu-black grid md:grid-cols-[1fr_0.9fr]">
          <div className="p-8 md:p-10 flex flex-col justify-center">
            <div className="chip mb-3 inline-flex items-center gap-2 w-fit"><Car size={13} /> ATTENDEE PARKING</div>
            <h2 className="font-display text-4xl md:text-5xl text-cbtu-cream leading-tight">COMPLIMENTARY <span className="gold-text-fill">PARKING</span></h2>
            <p className="text-cbtu-text/90 mt-3 leading-relaxed">Follow the instructions on this flyer to receive complimentary self-parking at the DoubleTree Jacksonville Riverfront.</p>
            <div className="mt-5 rounded-sm bg-gold text-cbtu-black px-6 py-4 w-fit">
              <div className="font-heading text-[10px] tracking-widest">VALIDATION CODE</div>
              <div className="font-display text-5xl tracking-wider" data-testid="parking-code">CBTU28</div>
            </div>
            <ol className="mt-5 space-y-2 text-sm text-cbtu-cream">
              <li><span className="text-gold font-semibold">1.</span> Scan a garage QR code before you leave the garage for the first time. Codes are also at the front desk, valet stand and meeting rooms.</li>
              <li><span className="text-gold font-semibold">2.</span> Enter your name, phone number, tag #, arrival date, overnight or hourly, and departure date.</li>
              <li><span className="text-gold font-semibold">3.</span> Enter validation code <span className="text-gold font-semibold">CBTU28</span> and click <em>Apply</em>.</li>
              <li><span className="text-gold font-semibold">4.</span> Click <em>Start Parking</em>. You should see the group's negotiated rate.</li>
            </ol>
            <p className="mt-4 text-xs text-orange-200 flex gap-2"><AlertTriangle size={14} className="flex-shrink-0 mt-0.5" /> The garage reads license plates. If no payment or parking code is entered, a parking citation will be mailed.</p>
            <p className="mt-2 text-xs text-cbtu-muted">No smartphone? A parking attendant at the hotel entrance can help you on their iPad.</p>
            <a href={ASSETS.parkingFlyer} target="_blank" rel="noreferrer" className="gold-button mt-6 px-6 py-3 rounded-sm inline-flex items-center gap-2 w-fit" data-testid="parking-flyer-download"><Download size={16} /> Open / Save Parking Flyer</a>
          </div>
          <a href={ASSETS.parkingFlyer} target="_blank" rel="noreferrer" className="block bg-cbtu-black order-first md:order-none" aria-label="Open complimentary parking flyer full size">
            <img src={ASSETS.parkingFlyer} alt="Complimentary parking flyer for CBTU Region III conference attendees, validation code CBTU28" className="w-full h-auto block" data-testid="parking-flyer-img" />
          </a>
        </div>
      </section>

      {/* Souvenir Journal Flipbook */}
      <section className="max-w-7xl mx-auto px-4 py-14">
        <div className="card-dark rounded-md overflow-hidden grid md:grid-cols-[1.3fr_0.7fr]" data-testid="flipbook-feature">
          <div className="p-8 md:p-10 flex flex-col justify-center">
            <div className="chip mb-3 inline-flex items-center gap-2 w-fit"><BookOpen size={13} /> NEW · FLIP-THROUGH</div>
            <h2 className="font-display text-3xl md:text-4xl text-cbtu-cream leading-tight">
              2026 DIGITAL AD <span className="gold-text-fill">JOURNAL</span>
            </h2>
            <p className="text-cbtu-text/85 mt-3 leading-relaxed">
              Flip through the full-color 26-page conference journal, honoring our champions of labor,
              recognizing our sponsors, and celebrating four states united under one movement.
              {!journalOpen && <span className="block text-gold mt-2 font-semibold" data-testid="journal-release-note">Opens Saturday, October 31 at 6:00 PM.</span>}
            </p>
            <ul className="mt-4 space-y-1.5 text-sm text-cbtu-cream">
              <li className="flex items-center gap-2"><Star size={14} className="text-gold" fill="currentColor" /> 26 beautifully designed pages</li>
              <li className="flex items-center gap-2"><Star size={14} className="text-gold" fill="currentColor" /> Honoring our champions &amp; sponsors</li>
              <li className="flex items-center gap-2"><Star size={14} className="text-gold" fill="currentColor" /> {journalOpen ? 'Interactive flipbook, now open' : 'Interactive flipbook opens 10/31 at 6 PM'}</li>
            </ul>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/digital-journal#flipbook" className="gold-button px-6 py-3 rounded-sm inline-flex items-center gap-2" data-testid="flipbook-open-btn">
                <BookOpen size={16} /> {journalOpen ? 'Read the Journal' : 'Opens 10/31 · 6 PM'}
              </Link>
              {!RECAP && <a
                href="#sponsor"
                className="ghost-button px-6 py-3 rounded-sm inline-flex items-center gap-2"
              >
                <Award size={16} /> Advertise in Next Year's Journal
              </a>}
            </div>
          </div>
          <div
            aria-label="CBTU Region III 2026 Souvenir Journal cover (coming soon)"
            className="relative block min-h-[320px] overflow-hidden"
          >
            <img
              src={ASSETS.digitalJournalCover}
              alt="CBTU Region III 2026 Souvenir Journal cover - Jacksonville, Florida (preview, flipbook coming soon)"
              loading="lazy"
              className="w-full h-auto block"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-cbtu-black/60 via-transparent to-transparent md:block hidden"></div>
            <div className="absolute bottom-4 right-4 bg-cbtu-black/80 text-gold border border-gold/60 px-4 py-2 rounded-sm font-heading text-xs tracking-widest shadow-lg">
              {journalOpen ? 'NOW OPEN' : 'OPENS 10/31 · 6 PM'}
            </div>
          </div>
        </div>
      </section>

      {/* Agenda */}
      <section id="agenda" className="max-w-7xl mx-auto px-4 py-16 scroll-mt-28">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <div className="chip mb-2">PROGRAM</div>
            <h2 className="font-display text-4xl text-cbtu-cream">CONFERENCE <span className="gold-text-fill">AGENDA</span></h2>
            <p className="text-cbtu-text/85 max-w-2xl mt-3">Four days of general sessions, elections, workshops, GOTV action, and celebration in Jacksonville. <span className="text-cbtu-muted italic">* Agenda subject to change.</span></p>
          </div>
          <div className="flex flex-wrap gap-3">
            <AddFullConference agenda={CONFERENCE.agenda} />
            <a href={ASSETS.agendaPdf} target="_blank" rel="noreferrer" className="ghost-button px-5 py-2.5 rounded-sm text-sm inline-flex items-center gap-2" data-testid="agenda-pdf-btn"><FileText size={14} /> Download Full Agenda (PDF)</a>
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-5 items-start" data-testid="agenda-grid">
          {CONFERENCE.agenda.map((d) => (
            <div key={d.day} className="card-dark rounded-md overflow-hidden">
              <div className="bg-cbtu-green/60 px-5 py-4 border-b border-gold/30 flex items-center gap-3">
                <Calendar size={22} className="text-gold" />
                <div className="font-heading tracking-widest text-gold text-lg md:text-xl">{d.day.toUpperCase()}</div>
              </div>
              <ul className="divide-y divide-gold/10">
                {d.items.map((it, idx) => (
                  <li key={`${d.day}-${it.time}-${it.title}`} className="px-5 py-4 grid grid-cols-[120px_1fr_auto] md:grid-cols-[170px_1fr_auto] gap-3 items-start">
                    <span className="font-heading text-sm md:text-base tracking-wider text-gold mt-0.5">{it.time}</span>
                    <div>
                      <div className="text-cbtu-cream text-base md:text-lg font-medium leading-snug">{it.title}</div>
                      {it.room && <div className="text-gold/90 text-sm md:text-base mt-1 inline-flex items-center gap-1.5"><MapPin size={15} /> {it.room}</div>}
                      {it.note && <div className="text-cbtu-text/75 text-sm md:text-base mt-1 italic">{it.note}</div>}
                    </div>
                    <AddToCalendar day={d} item={it} testId={`cal-${d.date}-${idx}`} />
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div className="mt-12" data-testid="day-signs">
          <h3 className="font-display text-3xl text-cbtu-cream mb-2">CONFERENCE <span className="gold-text-fill">MEETING ROOMS</span></h3>
          <p className="text-cbtu-muted text-sm mb-6">Where each day's meetings are held, and which meals are provided. Tap a sign to open, save or print it.</p>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
            {CONFERENCE.daySigns.map((s) => (
              <a key={s.src} href={s.src} target="_blank" rel="noreferrer" className="group block" data-testid={`day-sign-${s.src.split('/').pop().replace(/\.jpg.*/, '')}`}>
                <img src={s.src} alt={`${s.day}: ${s.label}`} loading="lazy" className="w-full rounded-md gold-border transition-transform duration-300 group-hover:-translate-y-1" />
                <div className="mt-2 font-heading text-[11px] tracking-widest text-gold">{s.day.toUpperCase()}</div>
                <div className="text-cbtu-cream text-sm">{s.label}</div>
              </a>
            ))}
          </div>
        </div>
        {CONFERENCE.activityFlyers.length > 0 && <div className="mt-14 scroll-mt-28" id="activities" data-testid="conference-activities">
          <h3 className="font-display text-3xl text-cbtu-cream mb-2">CONFERENCE <span className="gold-text-fill">ACTIVITIES</span></h3>
          <p className="text-cbtu-muted text-sm mb-6">Join us for these special events. Tap a flyer to open, save or share it.</p>
          <div className="grid sm:grid-cols-2 gap-6 max-w-4xl">
            {CONFERENCE.activityFlyers.map((s) => (
              <a key={s.src} href={s.src} target="_blank" rel="noreferrer" className="group block" data-testid={`activity-flyer-${s.src.split('/').pop().replace(/\.jpg.*/, '')}`}>
                <img src={s.src} alt={`${s.label}, ${s.day}`} loading="lazy" className="w-full rounded-md gold-border transition-transform duration-300 group-hover:-translate-y-1" />
                <div className="mt-3 font-heading text-xs tracking-widest text-gold">{s.day.toUpperCase()}</div>
                <div className="text-cbtu-cream text-lg font-medium">{s.label}</div>
                <div className="text-cbtu-text/80 text-sm inline-flex items-center gap-1"><MapPin size={13} className="text-gold" /> {s.place}</div>
              </a>
            ))}
          </div>
        </div>}

      </section>

      {!RECAP && <>
      {/* Sponsorship */}
      <section id="sponsor" className="bg-cbtu-navy/60 border-y border-gold/20">
        <div className="max-w-7xl mx-auto px-4 py-16">
          <div className="flex items-end justify-between gap-4 flex-wrap mb-8">
            <div>
              <div className="chip mb-2">PARTNER WITH US</div>
              <h2 className="font-display text-4xl text-cbtu-cream">SPONSORSHIP <span className="gold-text-fill">OPPORTUNITIES</span></h2>
              <p className="text-cbtu-text/85 max-w-2xl mt-3">Champion working people while reaching labor leaders from across Region III. Choose a sponsorship tier or place an ad in the souvenir journal.</p>
            </div>
            <div className="flex flex-wrap gap-3">
              <Link to="/digital-journal" className="gold-button px-5 py-2.5 rounded-sm text-sm inline-flex items-center gap-2" data-testid="conference-digital-journal-btn"><BookOpen size={14} /> View Digital Ad Journal</Link>
              <a href={ASSETS.sponsorshipSheet} target="_blank" rel="noreferrer" className="ghost-button px-5 py-2.5 rounded-sm text-sm inline-flex items-center gap-2"><FileText size={14} /> View Sponsor Sheet</a>
            </div>
          </div>
          {LATE && (
            <div className="mb-8 rounded-md border-2 border-gold/70 bg-cbtu-green/30 p-5 flex items-start gap-3" data-testid="sponsorship-digital-notice">
              <BookOpen size={20} className="text-gold mt-0.5 flex-shrink-0" />
              <div>
                <div className="font-display text-2xl text-cbtu-cream">PRINTED JOURNAL DEADLINE HAS PASSED</div>
                <p className="text-cbtu-text/90 text-sm mt-1">You can still take part as a sponsor or advertiser. New sponsorships and ads will appear in the <span className="text-gold font-semibold">digital ad journal on the CBTU Region III website</span>, not in the printed souvenir journal. Questions? Email <a href="mailto:cbturegioniii@gmail.com" className="text-gold underline">cbturegioniii@gmail.com</a>.</p>
              </div>
            </div>
          )}
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-4">
            {CONFERENCE.sponsorships.map((s) => {
              const Icon = tierIcon[s.tier] || Star;
              const grad = tierColor[s.tier] || 'from-amber-300 to-amber-600';
              return (
                <div key={s.tier} className="card-dark rounded-md overflow-hidden flex flex-col">
                  <div className={`bg-gradient-to-b ${grad} text-cbtu-black px-4 py-3 text-center`}>
                    <Icon size={22} className="mx-auto mb-1" />
                    <div className="font-display text-sm tracking-wide">{s.tier.toUpperCase()}</div>
                    <div className="font-display text-2xl">${s.price.toLocaleString()}</div>
                  </div>
                  <ul className="p-4 space-y-2 text-xs text-cbtu-text/90 flex-1">
                    {s.perks.map((p) => (
                      <li key={p} className="flex items-start gap-2"><CheckCircle2 size={12} className="text-gold mt-0.5 flex-shrink-0" /><span>{p}</span></li>
                    ))}
                  </ul>
                  {SALES_CLOSED ? <ClosedTag testId={`sponsor-closed-${s.tier.toLowerCase().replace(/\s+/g, '-')}`} /> : <a
                    href={s.payLink || 'mailto:cbturegioniii@gmail.com?subject=Sponsorship%20Inquiry'}
                    target={s.payLink ? '_blank' : undefined}
                    rel={s.payLink ? 'noopener noreferrer' : undefined}
                    data-testid={`sponsor-btn-${s.tier.toLowerCase().replace(/\s+/g, '-')}`}
                    className={`block text-center font-heading text-xs tracking-widest py-2.5 border-t border-gold/30 transition-colors ${s.payLink ? 'bg-[#2ca01c] hover:bg-[#248a16] text-white' : 'bg-cbtu-green/40 hover:bg-cbtu-green text-gold-bright'}`}
                  >
                    {s.payLink ? (LATE ? 'BUY NOW · DIGITAL LISTING' : 'BUY NOW · PAY ONLINE') : 'SPONSOR THIS TIER'}
                  </a>}
                </div>
              );
            })}
          </div>

          {/* Souvenir journal ads + Zelle payment */}
          <div className="mt-10 grid md:grid-cols-2 gap-6">
            <div className="card-dark p-6 rounded-md">
              <h3 className="font-display text-xl text-cbtu-cream mb-3">Souvenir Journal Advertisements</h3>
              {LATE && <p className="text-xs text-gold mb-2 italic" data-testid="journal-digital-note">New ads will appear in the digital ad journal on the website only, not the printed journal.</p>}
              <ul className="divide-y divide-gold/15">
                {CONFERENCE.journal.map((j) => (
                  <li key={j.type} className="flex items-center justify-between py-3 text-sm gap-3">
                    <span className="text-cbtu-cream flex-1">{j.type}</span>
                    <span className="font-display text-lg gold-text-fill min-w-[48px] text-right">${j.price}</span>
                    {SALES_CLOSED && <span className="font-heading text-[10px] tracking-widest text-cbtu-muted" data-testid={`journal-closed-${j.type.toLowerCase().replace(/\s+/g, '-')}`}>CLOSED</span>}
                    {!SALES_CLOSED && j.payLink && (
                      <a
                        href={j.payLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`journal-buynow-${j.type.toLowerCase().replace(/\s+/g, '-')}`}
                        className="inline-flex items-center gap-1 bg-[#2ca01c] hover:bg-[#248a16] text-white font-heading text-[10px] tracking-widest px-3 py-1.5 rounded-sm transition-colors"
                      >
                        BUY NOW <ExternalLink size={10} />
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            </div>
            <div className="card-dark p-6 rounded-md">
              <h3 className="font-display text-xl text-cbtu-cream mb-3">Individual Banquet Tickets</h3>
              <div className="flex items-baseline gap-2">
                <span className="font-display text-5xl gold-text-fill">$125</span>
                <span className="text-cbtu-muted text-sm">per person</span>
              </div>
              <p className="text-cbtu-text/85 mt-3 text-sm">Join us for the Champions of Labor Awards Banquet on Saturday evening, October 31, 2026.</p>
              {SALES_CLOSED && <div className="mt-4 font-heading text-xs tracking-widest text-cbtu-muted" data-testid="banquet-closed">BANQUET TICKET SALES ARE CLOSED</div>}
              {!SALES_CLOSED && <div className="mt-4 flex flex-wrap gap-2">
                {CONFERENCE.banquetTicketPayLink && (
                  <a
                    href={CONFERENCE.banquetTicketPayLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="banquet-buynow-btn"
                    className="inline-flex items-center gap-2 bg-[#2ca01c] hover:bg-[#248a16] text-white font-heading text-xs tracking-widest px-5 py-2.5 rounded-sm transition-colors"
                  >
                    BUY NOW · $125 <ExternalLink size={12} />
                  </a>
                )}
                <a href="#register" className="ghost-button inline-flex px-5 py-2.5 rounded-sm text-xs tracking-widest font-heading">PAY BY CHECK / ZELLE</a>
              </div>}
            </div>
          </div>

          {/* Zelle payment for Sponsorship / Journal / Banquet */}
          {!SALES_CLOSED && <div className="mt-6">
            <ZellePaymentCard title="Pay Sponsorships, Journal Ads & Banquet Tickets via Zelle or Cash App" />
          </div>}
        </div>
      </section>

      {/* Registration */}
      <section id="register" className="max-w-7xl mx-auto px-4 py-16">
        <div className="chip mb-2">REGISTER</div>
        <h2 className="font-display text-4xl text-cbtu-cream mb-2">CONFERENCE <span className="gold-text-fill">REGISTRATION</span></h2>
        <p className="text-cbtu-text/85 mb-8 max-w-2xl">Complete the form below to reserve your seat. We’ll follow up with payment instructions.</p>
        {LATE && <LateNotice />}

        <div className="grid lg:grid-cols-3 gap-6">
          <form onSubmit={handleSubmit} className="lg:col-span-2 card-dark p-6 rounded-md space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Full Name" required value={reg.name} onChange={(v) => setReg({ ...reg, name: v })} />
              <Field label="Email" type="email" required value={reg.email} onChange={(v) => setReg({ ...reg, email: v })} />
              <Field label="Cell / Phone" value={reg.phone} onChange={(v) => setReg({ ...reg, phone: v })} />
              <Field label="Local Chapter" value={reg.chapter} onChange={(v) => setReg({ ...reg, chapter: v })} placeholder="e.g., Jacksonville" />
              <div className="sm:col-span-2">
                <Field label="Union Affiliation / Local #" value={reg.union} onChange={(v) => setReg({ ...reg, union: v })} placeholder="e.g., ILA Local 1408" />
              </div>
            </div>

            <div>
              <label className="flex items-center gap-2 mb-3 cursor-pointer">
                <input type="checkbox" checked={reg.includeRegistration} onChange={(e) => setReg({ ...reg, includeRegistration: e.target.checked })} className="accent-amber-400 w-4 h-4" />
                <span className="text-xs tracking-widest text-gold font-heading">CONFERENCE REGISTRATION</span>
              </label>
              {reg.includeRegistration && (
                <>
                  <div className="grid sm:grid-cols-2 gap-2 pl-6">
                    {REGISTRATION_FEES.map((f) => (
                      <label key={f.category} className={`flex items-center justify-between gap-2 px-3 py-2.5 rounded-sm border cursor-pointer text-sm transition-colors ${reg.category === f.category ? 'border-gold bg-cbtu-green/30 text-cbtu-cream' : 'border-gold/20 text-cbtu-text/80 hover:border-gold/50'}`}>
                        <span className="flex items-center gap-2">
                          <input type="radio" name="category" value={f.category} checked={reg.category === f.category} onChange={() => setReg({ ...reg, category: f.category })} className="accent-amber-400" />
                          {f.category}
                        </span>
                        <span className="gold-text-fill font-display text-lg">${f.price + LATE_FEE}{LATE && <span className="block text-[10px] font-heading tracking-wider text-red-300 text-right">INCL. ${LATE_FEE} LATE FEE</span>}</span>
                      </label>
                    ))}
                  </div>

                  {REGISTRATION_FEES.find((f) => f.category === reg.category)?.payLink && (
                    <div className="pl-6 mt-3 flex flex-wrap items-center gap-3 bg-cbtu-green/20 border border-gold/20 rounded-sm px-4 py-3">
                      <div className="text-xs text-cbtu-text/85 flex-1 min-w-[180px]">
                        Pay by credit or debit card instantly — <span className="text-gold">no need to mail a check</span>.
                      </div>
                      <a
                        href={REGISTRATION_FEES.find((f) => f.category === reg.category).payLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`reg-buynow-${reg.category.toLowerCase().replace(/\s+/g, '-')}`}
                        className="inline-flex items-center gap-2 bg-[#2ca01c] hover:bg-[#248a16] text-white font-heading text-xs tracking-widest px-5 py-2.5 rounded-sm transition-colors"
                      >
                        BUY NOW · {reg.category.replace(' Registration', '').toUpperCase()} ${basePrice} <ExternalLink size={12} />
                      </a>
                      {LATE && LATE_REGISTRATION.payLink && (
                        <a href={LATE_REGISTRATION.payLink} target="_blank" rel="noopener noreferrer" data-testid="late-fee-buynow" className="inline-flex items-center gap-2 bg-[#2ca01c] hover:bg-[#248a16] text-white font-heading text-xs tracking-widest px-5 py-2.5 rounded-sm transition-colors">
                          BUY NOW · LATE FEE ${LATE_FEE} <ExternalLink size={12} />
                        </a>
                      )}
                      {LATE && (
                        <div className="w-full text-xs text-red-300" data-testid="late-fee-pay-note">
                          {LATE_REGISTRATION.payLink
                            ? `Please pay both the registration and the $${LATE_FEE} late fee.`
                            : `The $${LATE_FEE} late fee must also be paid, by Zelle or Cash App (below) or on-site.`}
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>

            {!SALES_CLOSED && <>
            <div>
              <label className="flex items-center gap-2 mb-2 cursor-pointer">
                <input type="checkbox" checked={reg.banquetQty > 0} onChange={(e) => setReg({ ...reg, banquetQty: e.target.checked ? 1 : 0 })} className="accent-amber-400 w-4 h-4" />
                <span className="text-xs tracking-widest text-gold font-heading">BANQUET TICKETS (${BANQUET_TICKET_PRICE} EACH)</span>
              </label>
              {reg.banquetQty > 0 && (
                <div className="pl-6 flex items-center gap-3">
                  <button type="button" onClick={() => setReg({ ...reg, banquetQty: Math.max(0, reg.banquetQty - 1) })} className="p-1.5 rounded-sm border border-gold/40 text-gold hover:bg-gold/10"><Minus size={14} /></button>
                  <span className="font-display text-xl gold-text-fill w-8 text-center">{reg.banquetQty}</span>
                  <button type="button" onClick={() => setReg({ ...reg, banquetQty: Math.min(50, reg.banquetQty + 1) })} className="p-1.5 rounded-sm border border-gold/40 text-gold hover:bg-gold/10"><Plus size={14} /></button>
                  <span className="text-sm text-cbtu-cream ml-2">= ${banquetTotal}</span>
                </div>
              )}
            </div>

            <div>
              <label className="flex items-center gap-2 mb-2 cursor-pointer">
                <input type="checkbox" checked={!!reg.sponsorship} onChange={(e) => setReg({ ...reg, sponsorship: e.target.checked ? SPONSORSHIP_TIERS[SPONSORSHIP_TIERS.length - 1].tier : '' })} className="accent-amber-400 w-4 h-4" />
                <span className="text-xs tracking-widest text-gold font-heading">SPONSORSHIP</span>
              </label>
              {reg.sponsorship && (
                <select value={reg.sponsorship} onChange={(e) => setReg({ ...reg, sponsorship: e.target.value })} className="w-full ml-6 max-w-md bg-cbtu-black border border-gold/30 focus:border-gold rounded-sm px-3 py-2.5 text-sm text-cbtu-cream outline-none">
                  {SPONSORSHIP_TIERS.map((s) => <option key={s.tier} value={s.tier}>{s.tier} — ${s.price.toLocaleString()}</option>)}
                </select>
              )}
            </div>

            <div>
              <label className="flex items-center gap-2 mb-2 cursor-pointer">
                <input type="checkbox" checked={!!reg.journalAd} onChange={(e) => setReg({ ...reg, journalAd: e.target.checked ? JOURNAL_AD_SIZES[JOURNAL_AD_SIZES.length - 1].size : '' })} className="accent-amber-400 w-4 h-4" />
                <span className="text-xs tracking-widest text-gold font-heading">SOUVENIR JOURNAL AD</span>
              </label>
              {reg.journalAd && (
                <select value={reg.journalAd} onChange={(e) => setReg({ ...reg, journalAd: e.target.value })} className="w-full ml-6 max-w-md bg-cbtu-black border border-gold/30 focus:border-gold rounded-sm px-3 py-2.5 text-sm text-cbtu-cream outline-none">
                  {JOURNAL_AD_SIZES.map((j) => <option key={j.size} value={j.size}>{j.size} — ${j.price}</option>)}
                </select>
              )}
            </div>
            </>}

            <div>
              <label className="block text-xs tracking-widest text-gold font-heading mb-2">PAYMENT METHOD</label>
              <select value={reg.paymentMethod} onChange={(e) => setReg({ ...reg, paymentMethod: e.target.value })} className="w-full max-w-md bg-cbtu-black border border-gold/30 focus:border-gold rounded-sm px-3 py-2.5 text-sm text-cbtu-cream outline-none">
                <option value="">— Select how you paid —</option>
                {PAYMENT_METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3 pt-2 border-t border-gold/15">
              <div className="text-cbtu-cream">Total: <span className="font-display text-3xl gold-text-fill ml-1">${total}</span></div>
              <button type="submit" disabled={submitting} className="gold-button px-6 py-3 rounded-sm inline-flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"><Ticket size={16} /> {submitting ? 'Submitting…' : 'Submit Registration'}</button>
            </div>
          </form>

          <aside className="card-dark p-6 rounded-md">
            <h3 className="font-display text-xl text-cbtu-cream mb-3">Mail-in Registration</h3>
            <p className="text-cbtu-text/85 text-sm mb-3">Prefer to mail? Print the official form and send with payment to:</p>
            <div className="text-cbtu-cream text-sm leading-relaxed mb-4">
              {REGISTRATION_MAIL.org}<br/>{REGISTRATION_MAIL.poBox}<br/>{REGISTRATION_MAIL.cityStateZip}
            </div>
            <a href={ASSETS.registrationForm} target="_blank" rel="noreferrer" className="ghost-button w-full px-4 py-2.5 rounded-sm text-sm inline-flex items-center justify-center gap-2"><Download size={14} /> View / Print Form</a>
            <div className="gold-divider my-5"></div>
            <div className="text-xs tracking-widest text-gold font-heading mb-2">PAYMENT METHODS</div>
            <div className="flex flex-wrap gap-1.5">
              {REGISTRATION_MAIL.paymentMethods.map((m) => (
                <span key={m} className="chip">{m}</span>
              ))}
            </div>
          </aside>
        </div>

        {/* Zelle payment for Registration */}
        <div className="mt-6 max-w-2xl">
          <ZellePaymentCard title="Pay Registration via Zelle or Cash App" />
        </div>
      </section>
      </>}

      {/* Venue */}
      <section className="bg-cbtu-navy/60 border-t border-gold/20">
        <div className="max-w-7xl mx-auto px-4 py-14 grid lg:grid-cols-2 gap-8 items-center">
          <div>
            <div className="chip mb-3">HOST HOTEL</div>
            <h2 className="font-display text-3xl text-cbtu-cream mb-2">DoubleTree by Hilton</h2>
            <p className="text-gold font-heading tracking-wide mb-3">Jacksonville Riverfront</p>
            <p className="text-cbtu-text/85 mb-4">{CONFERENCE.address}. On the Southbank of the St. Johns River, right on the Southbank Riverwalk with views of downtown Jacksonville.</p>
            {LATE && <HotelNotice />}
            <a href="https://group.doubletree.com/kljs6q" target="_blank" rel="noreferrer" className="ghost-button inline-flex items-center gap-2 px-5 py-2.5 rounded-sm text-sm">Reserve Your Room <ExternalLink size={14} /></a>
          </div>
          <a href="https://group.doubletree.com/kljs6q" target="_blank" rel="noreferrer" aria-label="Open the DoubleTree Jacksonville reservation page" className="rounded-md overflow-hidden gold-border block relative group" data-testid="doubletree-image">
            <img
              src={ASSETS.doubletreeHotel}
              alt="DoubleTree by Hilton Jacksonville Riverfront - CBTU Region III Host Hotel"
              className="w-full h-auto block transition-transform duration-500 group-hover:scale-[1.02]"
              loading="lazy"
            />
            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-cbtu-black/80 via-cbtu-black/30 to-transparent p-5">
              <div className="font-heading tracking-widest text-gold text-[10px] mb-1">HOST HOTEL</div>
              <div className="font-display text-xl text-cbtu-cream">DOUBLETREE BY HILTON</div>
              <div className="text-cbtu-muted text-xs">Jacksonville Riverfront · Florida’s First Coast</div>
            </div>
          </a>
        </div>
      </section>
    </div>
  );
}

function Field({ label, value, onChange, type = 'text', required, placeholder }) {
  return (
    <label className="block">
      <span className="block text-xs tracking-widest text-gold font-heading mb-1">{label}{required && <span className="text-red-400"> *</span>}</span>
      <input
        type={type}
        value={value}
        required={required}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-cbtu-black border border-gold/30 focus:border-gold rounded-sm px-3 py-2.5 text-sm text-cbtu-cream outline-none focus:ring-2 focus:ring-gold/40"
      />
    </label>
  );
}
